import type { NodeProps } from "@xyflow/react";
import { Handle, Position } from "@xyflow/react";

import type { TreeNode } from "../types";

const shapeStyle = (shape: TreeNode["shape"]): React.CSSProperties => {
  switch (shape) {
    case "rect":
      return { borderRadius: 0 };
    case "rounded":
      return { borderRadius: 8 };
    case "ellipse":
      return { borderRadius: "50%" };
    case "diamond":
      return { transform: "rotate(45deg)", borderRadius: 4 };
    case "hex":
      return {
        clipPath:
          "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)",
      };
  }
};

const aggGlyph = (state: TreeNode["agg_state"]) => {
  if (state === "checked") return "✓";
  if (state === "mixed") return "◐";
  return "○";
};

const aggColor = (state: TreeNode["agg_state"]) => {
  if (state === "checked") return "#16a34a";
  if (state === "mixed") return "#ca8a04";
  return "#9ca3af";
};

interface MindNodeData {
  node: TreeNode;
  onToggle: (n: TreeNode) => void;
  selected: boolean;
}

export function MindNode(props: NodeProps) {
  const { node, onToggle, selected } = props.data as unknown as MindNodeData;
  const isLeaf = node.children.length === 0;
  const style: React.CSSProperties = {
    minWidth: 140,
    minHeight: 40,
    padding: "8px 12px",
    background: node.color,
    color: "#111",
    border: selected ? "2px solid #2563eb" : "1px solid #94a3b8",
    fontSize: node.font_size,
    display: "flex",
    alignItems: "center",
    gap: 8,
    ...shapeStyle(node.shape),
  };
  const inner = node.shape === "diamond"
    ? { transform: "rotate(-45deg)", display: "flex", alignItems: "center", gap: 8 }
    : { display: "flex", alignItems: "center", gap: 8, width: "100%" };

  const handleAggClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLeaf) onToggle(node);
  };

  return (
    <div style={style}>
      <Handle type="target" position={Position.Left} style={{ background: "transparent", border: "none" }} />
      <div style={inner}>
        <span
          onClick={handleAggClick}
          title={isLeaf ? "Toggle checked" : `Aggregate: ${node.agg_state}`}
          style={{
            cursor: isLeaf ? "pointer" : "default",
            color: aggColor(node.agg_state),
            fontWeight: 700,
            fontSize: Math.max(14, node.font_size),
            width: 16,
            display: "inline-block",
          }}
        >
          {aggGlyph(node.agg_state)}
        </span>
        <span
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            textDecoration: node.checked && isLeaf ? "line-through" : "none",
            opacity: node.checked && isLeaf ? 0.6 : 1,
          }}
        >
          {node.title || <em style={{ opacity: 0.5 }}>(untitled)</em>}
        </span>
        {(node.note && node.note.trim().length > 0) || node.comment_count > 0 ? (
          <span
            style={{
              fontSize: Math.max(11, node.font_size - 2),
              color: "#64748b",
              marginLeft: "auto",
              paddingLeft: 4,
              display: "inline-flex",
              gap: 4,
            }}
          >
            {node.note && node.note.trim().length > 0 && (
              <span title={`Has note (${node.note.length} chars)`}>📝</span>
            )}
            {node.comment_count > 0 && (
              <span title={`${node.comment_count} comment(s)`}>
                💬{node.comment_count > 1 ? node.comment_count : ""}
              </span>
            )}
          </span>
        ) : null}
      </div>
      <Handle type="source" position={Position.Right} style={{ background: "transparent", border: "none" }} />
    </div>
  );
}
